import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Signal, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Observable } from 'rxjs';

import { AiCostCall } from '../../core/admin/ai-cost.types';
import { AiGuidanceNote } from '../../core/admin/ai-guidance.service';
import { ModalComponent } from '../modal/modal';
import { placeRefusal } from '../modal/modal-refusal';

export interface AiNoteDialogData {
  readonly responses: AiCostCall[];
  readonly save: (text: string, responseRef: string) => Observable<unknown>;
}

/** Write one note: closes only once saved, so a refusal leaves what was typed. */
@Component({
  selector: 'app-ai-note-dialog',
  imports: [
    DatePipe,
    ModalComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  template: `
    <app-modal
      heading="Add a note for the AI"
      submitLabel="Save note"
      [formGroup]="form"
      [pending]="pending()"
      [error]="error()"
      (submitted)="save()"
    >
      <div class="modal-fields">
        <mat-form-field appearance="outline">
          <mat-label>The AI response this is about</mat-label>
          <mat-select formControlName="response_ref" data-testid="ai-note-response">
            @for (call of data.responses; track call.short_id) {
              <mat-option [value]="call.short_id">
                {{ call.purpose_display }} · {{ call.created_at | date: 'd MMM y, HH:mm' }}
              </mat-option>
            }
          </mat-select>
          @if (form.controls.response_ref.hasError('serverError')) {
            <mat-error>{{ form.controls.response_ref.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Choose the response this is about.</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>What would have made it better</mat-label>
          <textarea
            matInput
            rows="5"
            maxlength="4000"
            formControlName="text"
            data-testid="ai-note-text"
          ></textarea>
          @if (form.controls.text.hasError('serverError')) {
            <mat-error>{{ form.controls.text.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Say what would have made it better.</mat-error>
          }
        </mat-form-field>
      </div>
    </app-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiNoteDialogComponent {
  protected readonly data = inject<AiNoteDialogData>(MAT_DIALOG_DATA);
  private readonly ref = inject(MatDialogRef<AiNoteDialogComponent>);

  protected readonly form = inject(FormBuilder).nonNullable.group({
    // The newest response is what a reviewer has just read.
    response_ref: [this.data.responses[0]?.short_id ?? '', Validators.required],
    text: ['', Validators.required],
  });
  protected readonly pending = signal(false);
  protected readonly error = signal<string | null>(null);

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { text, response_ref } = this.form.getRawValue();
    this.pending.set(true);
    this.error.set(null);
    this.data.save(text, response_ref).subscribe({
      next: () => this.ref.close(),
      error: (err: unknown) => {
        this.pending.set(false);
        this.error.set(placeRefusal(this.form, err, "Couldn't save the note. Please try again."));
      },
    });
  }
}

export interface AiNotesListData {
  readonly notes: Signal<AiGuidanceNote[]>;
  readonly canAdd: boolean;
  readonly add: () => void;
}

/** Your notes on this file, in a pop-up, as a context block opens. */
@Component({
  selector: 'app-ai-notes-list-dialog',
  imports: [DatePipe, MatButtonModule, ModalComponent],
  template: `
    <app-modal heading="Your notes for the AI">
      <ul class="note-list">
        @for (note of data.notes(); track note.id) {
          <li class="note-list__row">
            <p class="note-list__text">{{ note.text }}</p>
            <p class="note-list__meta">
              {{ note.status_display }} · {{ note.purpose_display }} ·
              {{ note.at | date: 'd MMM y' }}
            </p>
          </li>
        } @empty {
          <li class="note-list__meta">Nothing added yet.</li>
        }
      </ul>
      @if (data.canAdd) {
        <span modalFooterStart>
          <button mat-stroked-button type="button" (click)="data.add()">Add a note</button>
        </span>
      }
    </app-modal>
  `,
  styles: `
    .note-list {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .note-list__row {
      padding: var(--space-3) 0;
      border-bottom: 1px solid var(--border);
    }
    .note-list__text {
      margin: 0;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
    .note-list__meta {
      margin: var(--space-1) 0 0;
      color: var(--muted-foreground);
      font-size: var(--font-size-sm);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiNotesListDialogComponent {
  protected readonly data = inject<AiNotesListData>(MAT_DIALOG_DATA);
}
